import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { reaction } from 'mobx'
import { Button, Checkbox, Input, message, Modal, Segmented, Skeleton, Table, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table/interface'
import {
    ArrowDownOutlined,
    ArrowUpOutlined,
    CloseOutlined,
    DeleteOutlined,
    EyeInvisibleOutlined,
    EyeOutlined,
    FolderOpenOutlined,
    FolderOutlined,
    PlusOutlined,
    RightOutlined,
    SearchOutlined,
    SettingOutlined,
    ShoppingOutlined,
} from '@ant-design/icons'
import i from '../../translations/i'
import { storeLocal } from '../../helpers/stores'
import { CardList, cleanText, ListCard, ListEmpty, ListToolbar, ListToolbarField, ListToolbarSpacer, useNarrowScreen } from '../card_list/CardList'
import { VisibilityToggle } from '../visibility_toggle/VisibilityToggle'
import { CategoryStatus, CategoryTableController, categoryUrl } from './category_table.controller'
import '../product_table/product_table.less'

// Категории каталога — в том же виде, что и таблица товаров (ProductTable)

// Продавец (4) категории только смотрит
const readOnly = () => Number(storeLocal.get('role')) === 4

const SKELETON_ROWS = [-1, -2, -3, -4].map(id => ({ id, __skeleton: true }))

const categoryImage = (image: any): string | null => {
    if (!image) return null
    if (image.path) return image.path
    return image.uri ? `${image.base ?? ''}${image.uri}` : null
}

const copy = (value: string) => (e: React.MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard?.writeText(value).then(() => message.success(i.t('list:copied')))
}

const CategoryVisibility = observer(({ table, category }: { table: CategoryTableController, category: any }) => {
    const visible = Number(category.view) > 0

    return (
        <VisibilityToggle
            minimal
            checked={visible}
            disabled={readOnly()}
            loading={table.isBusy(category.id)}
            onLabel={i.t('productCards:inCatalog')}
            offLabel={i.t('productCards:hidden')}
            hint={visible ? i.t('categoryCards:toggleOnHint') : i.t('categoryCards:toggleOffHint')}
            onChange={checked => table.setActive(category.id, checked)}
        />
    )
})

const CategoryThumb = ({ category }: { category: any }) => {
    const image = categoryImage(category.image)

    return (
        <span className={`pt-thumb${image ? '' : ' pt-thumb--empty'}`}>
            {image ? <img src={image} alt="" loading="lazy" /> : category.subcategories ? <FolderOutlined /> : <ShoppingOutlined />}
        </span>
    )
}

// ID, CRM и что внутри категории: под названием в строке таблицы и в карточке
const CategoryMeta = ({ category }: { category: any }) => {
    const idCrm = cleanText(category.id_crm)

    return (
        <div className="pt-product__meta">
            <Tooltip title={i.t('categoryCards:idHint')} mouseEnterDelay={0.4}>
                <span className="pt-copy" onClick={copy(`${category.id}`)}>#{category.id}</span>
            </Tooltip>
            {idCrm &&
                <Tooltip title={i.t('productCards:crmHint')} mouseEnterDelay={0.4}>
                    <span className="pt-copy" onClick={copy(idCrm)}>CRM {idCrm}</span>
                </Tooltip>
            }
            {category.subcategories
                ? <span className="pt-tag pt-tag--info"><FolderOpenOutlined />{i.t('categoryCards:subcategories')}</span>
                : <span className="pt-tag"><ShoppingOutlined />{i.t('categoryCards:products')}</span>
            }
        </div>
    )
}

const CategoryCell = ({ table, category }: { table: CategoryTableController, category: any }) => {
    const name = cleanText(category.name)

    return (
        <div className="pt-product">
            <CategoryVisibility table={table} category={category} />
            <CategoryThumb category={category} />
            <div className="pt-product__body">
                <a className="pt-product__name" href={categoryUrl(category)} title={name}>
                    {name || i.t('productCards:noName')}
                </a>
                <CategoryMeta category={category} />
            </div>
        </div>
    )
}

// Порядок в каталоге и настройки категории
const CategoryMove = observer(({ table, category }: { table: CategoryTableController, category: any }) => {
    const disabled = readOnly() || table.isBusy(category.id)

    return (
        <span className="pt-move">
            {table.canMove && <>
                <Tooltip title={i.t('productCards:moveUp')}>
                    <Button type="text" size="small" icon={<ArrowUpOutlined />} disabled={disabled} onClick={() => table.move(category.id, 'up')} />
                </Tooltip>
                <Tooltip title={i.t('productCards:moveDown')}>
                    <Button type="text" size="small" icon={<ArrowDownOutlined />} disabled={disabled} onClick={() => table.move(category.id, 'down')} />
                </Tooltip>
            </>}
            <Tooltip title={i.t('categoryCards:edit')}>
                <Button type="text" size="small" icon={<SettingOutlined />} disabled={readOnly()} href={`/shop/catalog/category/edit/${category.id}`} />
            </Tooltip>
        </span>
    )
})

const CategoryActions = ({ table, category }: { table: CategoryTableController, category: any }) => (
    <span className="pt-actions">
        <CategoryMove table={table} category={category} />
        <Tooltip title={category.subcategories ? i.t('categoryCards:openSubcategories') : i.t('categoryCards:openProducts')}>
            <Button className="pt-open" type="text" shape="circle" icon={<RightOutlined />} href={categoryUrl(category)} />
        </Tooltip>
    </span>
)

// Категория на узком экране — то же, что строка таблицы: переключатель, порядок и отметка уходят в действия
const CategoryCard = observer(({ table, category }: { table: CategoryTableController, category: any }) => {
    const name = cleanText(category.name)
    const selected = table.selectedIds.includes(category.id)

    const toggleSelected = (checked: boolean) => table.setSelectedIds(checked
        ? [...table.selectedIds, category.id]
        : table.selectedIds.filter(id => id !== category.id)
    )

    return (
        <ListCard
            media={<CategoryThumb category={category} />}
            title={name || i.t('productCards:noName')}
            titleHint={name}
            href={categoryUrl(category)}
            muted={!(Number(category.view) > 0)}
            selected={selected}
            chips={<CategoryMeta category={category} />}
            actions={<>
                <CategoryVisibility table={table} category={category} />
                <CategoryMove table={table} category={category} />
                {!readOnly() && <Checkbox checked={selected} onChange={e => toggleSelected(e.target.checked)} />}
            </>}
        />
    )
})

// Отметить всю страницу для массовых действий — вместо отметки в шапке таблицы
const SelectPage = observer(({ table }: { table: CategoryTableController }) => {
    const ids = table.list.map((category: any) => category.id)
    const selected = ids.filter((id: number) => table.selectedIds.includes(id)).length

    return (
        <Checkbox
            checked={selected > 0 && selected === ids.length}
            indeterminate={selected > 0 && selected < ids.length}
            onChange={e => table.setSelectedIds(e.target.checked ? ids : [])}
        >
            {i.t('productCards:selectPage')}
        </Checkbox>
    )
})

const CategoryToolbar = observer(({ table }: { table: CategoryTableController }) => (
    <ListToolbar>
        <ListToolbarField wide>
            <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder={i.t('categoryCards:searchPlaceholder')}
                value={table.searchText}
                onChange={e => table.setSearch(e.target.value)}
            />
        </ListToolbarField>

        <ListToolbarSpacer />

        <Segmented
            value={table.status}
            options={[
                { label: i.t('productCards:statusAll'), value: 'all' },
                { label: i.t('productCards:statusActive'), value: 'active' },
                { label: i.t('productCards:statusHidden'), value: 'hidden' },
            ]}
            onChange={value => table.setStatus(value as CategoryStatus)}
        />

        {table.filtered &&
            <Button type="link" className="pt-reset" icon={<CloseOutlined />} onClick={() => table.resetFilters()}>
                {i.t('productCards:reset')}
            </Button>
        }
    </ListToolbar>
))

const BulkBar = observer(({ table }: { table: CategoryTableController }) => {
    const count = table.selectedIds.length
    if (count === 0) return null

    const confirmDelete = () => Modal.confirm({
        title: i.t('categoryCards:bulkDeleteConfirm', { count }),
        content: i.t('categoryCards:bulkDeleteText'),
        okText: i.t('productCards:bulkDelete'),
        okButtonProps: { danger: true },
        cancelText: i.t('def:no'),
        onOk: () => table.deleteSelected(),
    })

    return (
        <div className="pt-bulk">
            <span className="pt-bulk__count">{i.t('productCards:selected', { count })}</span>
            <div className="pt-bulk__actions">
                <Button size="small" icon={<EyeOutlined />} loading={table.bulkLoading} onClick={() => table.setActiveSelected(true)}>
                    {i.t('productCards:bulkShow')}
                </Button>
                <Button size="small" icon={<EyeInvisibleOutlined />} loading={table.bulkLoading} onClick={() => table.setActiveSelected(false)}>
                    {i.t('productCards:bulkHide')}
                </Button>
                <Button size="small" danger icon={<DeleteOutlined />} disabled={table.bulkLoading} onClick={confirmDelete}>
                    {i.t('productCards:bulkDelete')}
                </Button>
            </div>
            <Button size="small" type="text" className="pt-bulk__clear" icon={<CloseOutlined />} onClick={() => table.setSelectedIds([])}>
                {i.t('productCards:bulkClear')}
            </Button>
        </div>
    )
})

// Клик по строке открывает категорию; у ссылок, кнопок, переключателя, отметки и копируемых значений своё действие
const onRowClick = (category: any) => (e: React.MouseEvent<HTMLElement>) => {
    if (category.__skeleton) return
    const target = e.target as HTMLElement
    if (!e.currentTarget.contains(target)) return
    if (target.closest('a, button, input, label, [role="switch"], .pt-copy, .ant-checkbox-wrapper, .ant-table-selection-column')) return
    if (window.getSelection()?.toString()) return

    if (e.metaKey || e.ctrlKey) window.open(categoryUrl(category), '_blank')
    else window.location.href = categoryUrl(category)
}

export const CategoryTable = observer(({ table, createHref }: { table: CategoryTableController, createHref: string }) => {

    const narrow = useNarrowScreen()

    useEffect(() => {
        table.load()
        return reaction(() => table.url, url => window.history.replaceState(null, '', url))
    }, [table])

    const { list, loading } = table
    const firstLoad = loading && list.length === 0

    const empty = table.filtered
        ? {
            icon: <SearchOutlined />,
            title: i.t('productCards:empty'),
            text: i.t('categoryCards:emptyHint'),
            action: <Button onClick={() => table.resetFilters()}>{i.t('productCards:reset')}</Button>,
        }
        : {
            icon: <FolderOutlined />,
            title: i.t('categoryCards:emptyAll'),
            text: i.t('categoryCards:emptyAllHint'),
            action: !readOnly() && <Button type="primary" icon={<PlusOutlined />} href={createHref}>{i.t('categories:createCategory')}</Button>,
        }

    if (narrow) return (
        <section className="pt pt--cards">
            <div className="pt__top">
                <CategoryToolbar table={table} />
                <BulkBar table={table} />
            </div>

            <CardList
                items={list}
                count={table.total}
                loading={loading}
                extra={list.length > 0 && !readOnly() && <SelectPage table={table} />}
                renderItem={(category: any) => <CategoryCard table={table} category={category} />}
                empty={empty}
                pagination={{
                    current: table.page,
                    pageSize: table.limit,
                    total: table.total,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                        const pageChanged = page !== table.page
                        table.setPagination(page, pageSize)
                        if (pageChanged) window.scrollTo({ top: 0, behavior: 'smooth' })
                    },
                }}
            />
        </section>
    )

    const columns: ColumnsType<any> = [
        {
            key: 'name',
            title: i.t('categoryCards:col.category'),
            render: (category: any) => category.__skeleton
                ? <div className="pt-product">
                    <Skeleton.Button active size="small" shape="round" className="pt-skeleton-toggle" />
                    <Skeleton.Avatar active shape="square" size={52} />
                    <Skeleton active title={false} paragraph={{ rows: 2, width: ['50%', '25%'] }} />
                </div>
                : <CategoryCell table={table} category={category} />,
        },
        {
            key: 'actions',
            width: 160,
            align: 'right',
            render: (category: any) => !category.__skeleton && <CategoryActions table={table} category={category} />,
        },
    ]

    return (
        <section className="pt">
            <div className="pt__top">
                <CategoryToolbar table={table} />
                <BulkBar table={table} />
            </div>

            <Table
                className="pt__table"
                rowKey="id"
                columns={columns}
                dataSource={firstLoad ? SKELETON_ROWS : list}
                loading={{ spinning: loading && !firstLoad, delay: 150 }}
                rowClassName={(category: any) => [
                    'pt-row',
                    !category.__skeleton && !(Number(category.view) > 0) && 'pt-row--hidden',
                ].filter(Boolean).join(' ')}
                onRow={(category: any) => ({ onClick: onRowClick(category) })}
                rowSelection={readOnly() ? undefined : {
                    selectedRowKeys: table.selectedIds,
                    onChange: keys => table.setSelectedIds(keys as number[]),
                    getCheckboxProps: (category: any) => ({ disabled: !!category.__skeleton }),
                }}
                locale={{ emptyText: loading ? ' ' : <ListEmpty {...empty} /> }}
                pagination={{
                    current: table.page,
                    pageSize: table.limit,
                    total: table.total,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 20, 50, 100],
                    showTotal: (total, [from, to]) => i.t('productCards:range', { from, to, total: total.toLocaleString() }),
                    position: ['bottomRight'],
                }}
                onChange={pagination => {
                    const pageChanged = (pagination.current ?? 1) !== table.page
                    table.setPagination(pagination.current ?? 1, pagination.pageSize ?? table.limit)
                    if (pageChanged) window.scrollTo({ top: 0, behavior: 'smooth' })
                }}
            />
        </section>
    )
})
